import Feature from 'ol/Feature';
import type Map from 'ol/Map';
import LineString from 'ol/geom/LineString';
import Point from 'ol/geom/Point';
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import { Circle as CircleStyle, Fill, Stroke, Style, Text } from 'ol/style';

export const ELEVATION_OVERLAY_ROLE = 'elevation-overlay';

const routeStyle = (stale: boolean): Style[] => [
  new Style({ stroke: new Stroke({ color: 'rgba(255,255,255,.9)', width: 6 }) }),
  new Style({ stroke: new Stroke({ color: stale ? '#8a939c' : '#6a1b9a', width: 3, lineDash: [10, 6] }) }),
];
const endStyle = (text: string): Style => new Style({
  image: new CircleStyle({ radius: 7, fill: new Fill({ color: '#6a1b9a' }), stroke: new Stroke({ color: '#fff', width: 2 }) }),
  text: new Text({ text, font: '700 10px Inter, system-ui, sans-serif', fill: new Fill({ color: '#fff' }) }),
});
const hoverStyle = new Style({ image: new CircleStyle({ radius: 7, fill: new Fill({ color: '#ffb300' }), stroke: new Stroke({ color: '#1b1b1b', width: 2 }) }) });
const queryStyle = new Style({ image: new CircleStyle({ radius: 6, fill: new Fill({ color: '#fff' }), stroke: new Stroke({ color: '#6a1b9a', width: 3 }) }) });

/**
 * Temporary, map-only graphics for elevation tools: the profiled route, its A/B ends, the sample hovered on the chart
 * and the last queried point. Never part of the project, the Maestro or a .casurgeo; hidden in PNG exports.
 */
export class ElevationOverlay {
  private readonly source = new VectorSource();
  private readonly layer = new VectorLayer({ source: this.source, properties: { role: ELEVATION_OVERLAY_ROLE }, updateWhileInteracting: true });
  private readonly route = new Feature<LineString>();
  private readonly start = new Feature<Point>();
  private readonly end = new Feature<Point>();
  private readonly hover = new Feature<Point>();
  private readonly query = new Feature<Point>();

  constructor(map: Map) {
    this.layer.setZIndex(20_010);
    this.start.setStyle(endStyle('A')); this.end.setStyle(endStyle('B'));
    this.hover.setStyle(hoverStyle); this.query.setStyle(queryStyle);
    map.addLayer(this.layer);
  }

  showRoute(coordinates: readonly (readonly number[])[], stale = false): void {
    this.clearRoute();
    if (coordinates.length < 2) return;
    this.route.setGeometry(new LineString(coordinates.map((coordinate) => [...coordinate])));
    this.route.setStyle(routeStyle(stale));
    this.start.setGeometry(new Point([...coordinates[0]!])); this.end.setGeometry(new Point([...coordinates[coordinates.length - 1]!]));
    this.source.addFeatures([this.route, this.start, this.end]);
  }

  setStale(stale: boolean): void { if (this.route.getGeometry()) this.route.setStyle(routeStyle(stale)); }

  setHover(coordinate: readonly number[] | null): void {
    if (!coordinate) { if (this.source.hasFeature(this.hover)) this.source.removeFeature(this.hover); return; }
    this.hover.setGeometry(new Point([...coordinate]));
    if (!this.source.hasFeature(this.hover)) this.source.addFeature(this.hover);
  }

  setQuery(coordinate: readonly number[] | null): void {
    if (!coordinate) { if (this.source.hasFeature(this.query)) this.source.removeFeature(this.query); return; }
    this.query.setGeometry(new Point([...coordinate]));
    if (!this.source.hasFeature(this.query)) this.source.addFeature(this.query);
  }

  clearRoute(): void {
    [this.route, this.start, this.end, this.hover].forEach((feature) => { if (this.source.hasFeature(feature)) this.source.removeFeature(feature); });
    this.route.setGeometry(undefined);
  }

  /** Number of graphics on the map (tests: nothing must be left behind after closing). */
  get featureCount(): number { return this.source.getFeatures().length; }
}
